import { Box, Link, List, ListItem, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { Seo } from '../components/seo/Seo';
import { buildCanonicalUrl } from '../utils/seo/canonical';
import { organization, breadcrumbList } from '../utils/seo/jsonLd';

/**
 * About page (#217). A public, indexable page mixing promotional and
 * informative tone: why Round Recon exists, who it's for, how to get the
 * most from it, and a transparent (and disclaimed) account of how it finds
 * products. SEO is a build requirement (§16.4) — this page carries its own
 * title/description/canonical and Organization + BreadcrumbList JSON-LD.
 */
export function AboutPage() {
  return (
    <>
      <Seo
        title="About Round Recon"
        description="Round Recon is an independent ammunition price-comparison engine. Learn why it exists, who it's for, and how it aggregates in-stock listings from retailers across the web."
        canonical={buildCanonicalUrl('/about')}
        jsonLd={[
          organization(),
          breadcrumbList([
            { name: 'Home', url: '/' },
            { name: 'About', url: '/about' },
          ]),
        ]}
      />
      <Stack spacing={3} sx={{ maxWidth: 'md' }}>
        <Typography variant="h1" component="h1">
          About Round Recon
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'text.secondary' }}
        >
          Round Recon is an independent search engine for in-stock ammunition. We gather listings
          from retailers across the web, normalize their wildly inconsistent product data into one
          comparable format, and put strong filters and price-per-round math in front of you — so
          you can find the right round at the right price in seconds instead of opening twenty tabs.
        </Typography>

        <Box component="section" aria-labelledby="why">
          <Typography id="why" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
            Why we exist
          </Typography>
          <Typography variant="body1">
            Ammunition pricing is a mess. The same cartridge is listed a dozen ways across a dozen
            stores, &ldquo;in stock&rdquo; often isn&apos;t, and the real cost — price per round,
            once you account for case count — is buried. Round Recon exists to make that market
            legible: one place to compare what&apos;s actually available right now, ranked by
            genuine value rather than whoever spent the most on ads.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="who">
          <Typography id="who" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
            Who it&apos;s for
          </Typography>
          <Typography variant="body1">
            Whether you&apos;re a high-volume competitive shooter watching cost per round, a hunter
            stocking a specific load before season, or someone who just wants a fair price without
            the hassle, Round Recon is built for you. It&apos;s free to use, and you never have to
            create an account to search.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="get-the-most">
          <Typography
            id="get-the-most"
            variant="h2"
            component="h2"
            sx={{ fontSize: '1.4rem', mb: 1 }}
          >
            Getting the most out of it
          </Typography>
          <List sx={{ pl: 2, listStyleType: 'disc' }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Filter hard.</strong> Narrow by caliber, bullet type, case material, grain
              weight, and use case to cut a thousand listings down to the handful that fit.
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Sort by price per round.</strong> The sticker price hides the real cost — sort
              by{' '}
              <Link component={RouterLink} to="/search?sort=pricePerRound&sortDir=asc">
                best price per round
              </Link>{' '}
              to compare apples to apples.
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Watch the freshness label.</strong> Every listing shows how recently we
              confirmed it. Prefer <em>fresh</em> listings and always verify on the retailer&apos;s
              site before buying.
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Create alerts.</strong> With a free{' '}
              <Link component={RouterLink} to="/register">
                account
              </Link>
              , save searches and get notified when a cartridge you want drops into stock or under a
              target price.
            </ListItem>
          </List>
        </Box>

        <Box component="section" aria-labelledby="how-it-works">
          <Typography
            id="how-it-works"
            variant="h2"
            component="h2"
            sx={{ fontSize: '1.4rem', mb: 1 }}
          >
            How we find products
          </Typography>
          <Typography variant="body1">
            We continuously aggregate publicly available product listings from many ammunition
            retailers. Because every store describes its products differently, we normalize each
            listing into a consistent schema — caliber, bullet type, grain weight, round count, and
            price — and compute a comparable price per round. Sponsored placements, when present,
            are always clearly labeled as such and held to the same relevance and freshness
            standards as organic results. We are not a retailer: we never sell ammunition, never
            process payments, and never handle shipping or eligibility — those always happen on the
            retailer&apos;s own site.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="accuracy">
          <Typography id="accuracy" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
            Accuracy and responsibility
          </Typography>
          <Typography variant="body1">
            We work hard to keep listings current, but prices, availability, and product details
            change constantly and can be wrong or out of date by the time you see them. Round Recon
            provides this information &ldquo;as is,&rdquo; without warranty of any kind, and is not
            responsible for inaccuracies, pricing errors, or stock changes on retailer sites.{' '}
            <strong>
              Always confirm the price, specifications, availability, and your own legal eligibility
              on the retailer&apos;s website before purchasing.
            </strong>{' '}
            See our{' '}
            <Link component={RouterLink} to="/responsibility">
              user responsibility
            </Link>{' '}
            and{' '}
            <Link component={RouterLink} to="/terms">
              terms
            </Link>{' '}
            for details.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="get-started">
          <Typography
            id="get-started"
            variant="h2"
            component="h2"
            sx={{ fontSize: '1.4rem', mb: 1 }}
          >
            Get started
          </Typography>
          <Typography variant="body1">
            Ready to find a better price? Head to the{' '}
            <Link component={RouterLink} to="/search">
              listings
            </Link>{' '}
            and search for your caliber — no account required.
          </Typography>
        </Box>
      </Stack>
    </>
  );
}

export default AboutPage;
